import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, CreditCard, Truck, Shield, CheckCircle, Banknote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { trackCheckoutStart, trackPurchase, saveAbandonedCart } from '../lib/analytics';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Checkout = ({ cartItems, getCartTotal, clearCart }) => {
  const navigate = useNavigate();
  const { customer, token, isAuthenticated } = useCustomerAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);

  // ---- Coupon state ----
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);   // { code, discount_percent, discount_amount }
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  // Payment method: 'online' (Razorpay) or 'cod' (Cash on Delivery)
  const [paymentMethod, setPaymentMethod] = useState('online');
  const subtotal = getCartTotal();
  const shippingFee = subtotal >= 2999 ? 0 : 99;
  const discountAmount = appliedCoupon?.discount_amount || 0;
  const finalTotal = Math.max(0, subtotal - discountAmount) + shippingFee;

  const applyCoupon = async (code) => {
    const c = (code || couponInput).trim().toUpperCase();
    if (!c) return;
    // Non-stackable guard: if a coupon is already applied and the customer is
    // trying to add a DIFFERENT one, block it with a clear message. Re-applying
    // the same code is a no-op.
    if (appliedCoupon && appliedCoupon.code !== c) {
      const msg = 'Only one promotional coupon can be used per order. Remove the current coupon to apply a different one.';
      setCouponError(msg);
      toast.error(msg);
      return;
    }
    setCouponLoading(true);
    setCouponError('');
    try {
      const res = await axios.post(`${API}/coupons/validate`, { code: c, cart_total: subtotal });
      if (res.data?.valid) {
        setAppliedCoupon({
          code: c,
          discount_percent: res.data.coupon?.discount_percent,
          discount_amount: res.data.discount_amount,
        });
        setCouponInput(c);
        localStorage.setItem('ss_applied_coupon', c);
        toast.success(`Coupon ${c} applied — you save ₹${res.data.discount_amount}`);
      } else {
        setAppliedCoupon(null);
        setCouponError(res.data?.message || 'Invalid coupon');
      }
    } catch (e) {
      setAppliedCoupon(null);
      setCouponError(e.response?.data?.detail || 'Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
    localStorage.removeItem('ss_applied_coupon');
  };

  // Auto-apply: if user previously copied a code (banner or exit popup), try it
  useEffect(() => {
    const stored = localStorage.getItem('ss_applied_coupon');
    if (stored && !appliedCoupon && subtotal > 0) {
      applyCoupon(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const [shippingAddress, setShippingAddress] = useState({
    name: customer?.name || '',
    phone: customer?.phone || '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India'
  });

  // Fire begin_checkout once when the user lands on the page with items in cart.
  const beginCheckoutFired = useRef(false);
  useEffect(() => {
    if (!beginCheckoutFired.current && cartItems && cartItems.length > 0) {
      beginCheckoutFired.current = true;
      trackCheckoutStart(cartItems, getCartTotal());
    }
  }, [cartItems, getCartTotal]);

  // Abandoned-cart capture: save snapshot 3s after user pauses typing email/phone,
  // and again on tab close. Cleared once order completes.
  const saveAbandonedRef = useRef(false);
  useEffect(() => {
    if (orderComplete) return;
    const email = (customer?.email || '').trim();
    const phone = (shippingAddress.phone || '').trim();
    if (!email && !phone) return;
    if (!cartItems || cartItems.length === 0) return;

    const payload = {
      name: shippingAddress.name || customer?.name || '',
      email,
      phone,
      items: cartItems.map(i => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity })),
      total_amount: getCartTotal(),
    };

    const t = setTimeout(() => {
      saveAbandonedCart(payload);
      saveAbandonedRef.current = true;
    }, 3000);

    const onUnload = () => {
      try {
        const url = `${API}/analytics/abandoned-cart`;
        const body = JSON.stringify({ session_id: localStorage.getItem('ss_visitor_id') || '', ...payload });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        }
      } catch {}
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      clearTimeout(t);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [shippingAddress.phone, shippingAddress.name, customer?.email, customer?.name, cartItems, getCartTotal, orderComplete]);

  const handleAddressChange = (e) => {
    setShippingAddress({ ...shippingAddress, [e.target.name]: e.target.value });
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to continue');
      navigate('/login', { state: { from: { pathname: '/checkout' } } });
      return;
    }

    // Validate shipping address
    const requiredFields = ['name', 'phone', 'address_line1', 'city', 'state', 'pincode'];
    for (const field of requiredFields) {
      if (!shippingAddress[field]) {
        toast.error(`Please fill in ${field.replace('_', ' ')}`);
        return;
      }
    }

    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setIsProcessing(true);

    const orderData = {
      items: cartItems.map(item => ({
        product_id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image: item.image
      })),
      shipping_address: shippingAddress,
      total_amount: getCartTotal(),
      coupon_code: appliedCoupon?.code || null,
      payment_method: paymentMethod,
    };

    // ---------- Cash on Delivery branch ----------
    if (paymentMethod === 'cod') {
      try {
        const res = await axios.post(`${API}/orders/create-cod-order`, orderData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.status === 'success') {
          setCompletedOrder(res.data.order);
          setOrderComplete(true);
          try {
            trackPurchase(
              res.data.order?.order_id,
              cartItems,
              res.data.order?.total_amount || getCartTotal()
            );
          } catch {}
          clearCart();
          toast.success('Order placed successfully! Pay on delivery.');
        }
      } catch (error) {
        const detail = error.response?.data?.detail;
        if (detail && typeof detail === 'object' && Array.isArray(detail.items)) {
          const names = detail.items.map(i => i.name).join(', ');
          toast.error(`Out of stock: ${names}. Please remove these items to proceed.`);
        } else {
          toast.error(typeof detail === 'string' ? detail : 'Failed to place order');
        }
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // ---------- Online (Razorpay) branch ----------
    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        setIsProcessing(false);
        return;
      }

      const response = await axios.post(`${API}/orders/create-razorpay-order`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { order_id, razorpay_order_id, razorpay_key_id, amount } = response.data;

      // Initialize Razorpay
      const options = {
        key: razorpay_key_id,
        amount: amount,
        currency: 'INR',
        name: 'Sachdeva Sons',
        description: `Order #${order_id}`,
        order_id: razorpay_order_id,
        handler: async (paymentResponse) => {
          try {
            // Verify payment
            const verifyResponse = await axios.post(`${API}/orders/verify-payment`, {
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });

            if (verifyResponse.data.status === 'success') {
              setCompletedOrder(verifyResponse.data.order);
              setOrderComplete(true);
              try {
                trackPurchase(
                  verifyResponse.data.order?.order_id,
                  cartItems,
                  verifyResponse.data.order?.total_amount || getCartTotal()
                );
              } catch {}
              clearCart();
              toast.success('Order placed successfully!');
            }
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: shippingAddress.name,
          email: customer?.email || '',
          contact: shippingAddress.phone
        },
        theme: {
          color: '#C4B5A0'
        },
        modal: {
          ondismiss: () => {
            setIsProcessing(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.items)) {
        const names = detail.items.map(i => i.name).join(', ');
        toast.error(`Out of stock: ${names}. Please remove these items to proceed.`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to initiate payment');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (orderComplete && completedOrder) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] pt-32 pb-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="bg-white border-none shadow-lg p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-3xl font-seasons text-[#2C2C2C] mb-2">Order Confirmed!</h1>
            <p className="text-[#8B7D6B] font-montserrat mb-6">
              Thank you for your order. We'll send you a confirmation email shortly.
            </p>
            
            <div className="bg-[#F5F3F0] rounded-lg p-6 mb-6 text-left">
              <p className="font-montserrat text-sm text-[#6B4E37] mb-2">Order ID</p>
              <p className="font-seasons text-xl text-[#2C2C2C] mb-4">{completedOrder.order_id}</p>
              
              <p className="font-montserrat text-sm text-[#6B4E37] mb-2">Total Amount</p>
              <p className="font-seasons text-xl text-[#2C2C2C]">₹{completedOrder.total_amount}</p>
            </div>

            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate('/account/orders')}
                className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white"
              >
                View Orders
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-[#C4B5A0] text-[#C4B5A0]"
              >
                Continue Shopping
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F7] pt-32 pb-16">
      <div className="container mx-auto px-4">
        <Button
          variant="ghost"
          className="mb-6 text-[#2C2C2C] hover:text-[#C4B5A0]"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <h1 className="text-4xl font-seasons text-[#2C2C2C] mb-8">Checkout</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Shipping Address */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white border-none shadow-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="h-5 w-5 text-[#C4B5A0]" />
                <h2 className="text-xl font-seasons text-[#2C2C2C]">Shipping Address</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-montserrat text-[#6B4E37] mb-2">Full Name *</label>
                  <Input
                    name="name"
                    value={shippingAddress.name}
                    onChange={handleAddressChange}
                    className="bg-[#F5F3F0] border-none"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-montserrat text-[#6B4E37] mb-2">Phone *</label>
                  <Input
                    name="phone"
                    value={shippingAddress.phone}
                    onChange={handleAddressChange}
                    className="bg-[#F5F3F0] border-none"
                    placeholder="+91 9876543210"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-montserrat text-[#6B4E37] mb-2">Address Line 1 *</label>
                  <Input
                    name="address_line1"
                    value={shippingAddress.address_line1}
                    onChange={handleAddressChange}
                    className="bg-[#F5F3F0] border-none"
                    placeholder="House/Flat No., Building Name"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-montserrat text-[#6B4E37] mb-2">Address Line 2</label>
                  <Input
                    name="address_line2"
                    value={shippingAddress.address_line2}
                    onChange={handleAddressChange}
                    className="bg-[#F5F3F0] border-none"
                    placeholder="Street, Locality"
                  />
                </div>
                <div>
                  <label className="block text-sm font-montserrat text-[#6B4E37] mb-2">City *</label>
                  <Input
                    name="city"
                    value={shippingAddress.city}
                    onChange={handleAddressChange}
                    className="bg-[#F5F3F0] border-none"
                    placeholder="Delhi"
                  />
                </div>
                <div>
                  <label className="block text-sm font-montserrat text-[#6B4E37] mb-2">State *</label>
                  <Input
                    name="state"
                    value={shippingAddress.state}
                    onChange={handleAddressChange}
                    className="bg-[#F5F3F0] border-none"
                    placeholder="Delhi"
                  />
                </div>
                <div>
                  <label className="block text-sm font-montserrat text-[#6B4E37] mb-2">Pincode *</label>
                  <Input
                    name="pincode"
                    value={shippingAddress.pincode}
                    onChange={handleAddressChange}
                    className="bg-[#F5F3F0] border-none"
                    placeholder="110001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-montserrat text-[#6B4E37] mb-2">Country</label>
                  <Input
                    name="country"
                    value={shippingAddress.country}
                    disabled
                    className="bg-[#E8E5E0] border-none"
                  />
                </div>
              </div>
            </Card>

            {/* Payment Method */}
            <Card className="bg-white border-none shadow-lg p-6" data-testid="checkout-payment-method">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="h-5 w-5 text-[#C4B5A0]" />
                <h2 className="text-xl font-seasons text-[#2C2C2C]">Payment Method</h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('online')}
                  data-testid="payment-method-online"
                  aria-pressed={paymentMethod === 'online'}
                  className={`text-left border rounded-lg p-4 transition-all ${
                    paymentMethod === 'online'
                      ? 'border-[#6B4E37] bg-[#FAF6EE] ring-2 ring-[#C4B5A0]'
                      : 'border-[#E8E5E0] bg-white hover:border-[#C4B5A0]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 ${paymentMethod === 'online' ? 'border-[#6B4E37] bg-[#6B4E37]' : 'border-[#8B7D6B]'}`}>
                      {paymentMethod === 'online' && <div className="h-1.5 w-1.5 bg-white rounded-full m-auto mt-[3px]" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-[#6B4E37]" />
                        <span className="font-seasons text-[#2C2C2C]">Online Payment</span>
                      </div>
                      <p className="text-xs font-montserrat text-[#8B7D6B] mt-1">
                        UPI, Cards, Netbanking & Wallets via Razorpay
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setPaymentMethod('cod')}
                  data-testid="payment-method-cod"
                  aria-pressed={paymentMethod === 'cod'}
                  className={`text-left border rounded-lg p-4 transition-all ${
                    paymentMethod === 'cod'
                      ? 'border-[#6B4E37] bg-[#FAF6EE] ring-2 ring-[#C4B5A0]'
                      : 'border-[#E8E5E0] bg-white hover:border-[#C4B5A0]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 ${paymentMethod === 'cod' ? 'border-[#6B4E37] bg-[#6B4E37]' : 'border-[#8B7D6B]'}`}>
                      {paymentMethod === 'cod' && <div className="h-1.5 w-1.5 bg-white rounded-full m-auto mt-[3px]" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Banknote className="h-4 w-4 text-[#6B4E37]" />
                        <span className="font-seasons text-[#2C2C2C]">Cash on Delivery</span>
                      </div>
                      <p className="text-xs font-montserrat text-[#8B7D6B] mt-1">
                        Pay in cash at your doorstep
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </Card>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                <Truck className="h-6 w-6 text-[#C4B5A0] mx-auto mb-2" />
                <p className="text-xs font-montserrat text-[#6B4E37]">Free Shipping</p>
                <p className="text-xs text-[#8B7D6B]">Above ₹2,999</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                <Shield className="h-6 w-6 text-[#C4B5A0] mx-auto mb-2" />
                <p className="text-xs font-montserrat text-[#6B4E37]">Secure Payment</p>
                <p className="text-xs text-[#8B7D6B]">100% Protected</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                <CreditCard className="h-6 w-6 text-[#C4B5A0] mx-auto mb-2" />
                <p className="text-xs font-montserrat text-[#6B4E37]">Easy Returns</p>
                <p className="text-xs text-[#8B7D6B]">7 Day Policy</p>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="bg-white border-none shadow-lg p-6 sticky top-24">
              <h2 className="text-xl font-seasons text-[#2C2C2C] mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex gap-3">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-20 object-cover rounded"
                    />
                    <div className="flex-1">
                      <p className="font-montserrat text-sm text-[#2C2C2C] line-clamp-2">{item.name}</p>
                      <p className="text-xs text-[#8B7D6B]">Qty: {item.quantity}</p>
                      <p className="font-seasons text-[#2C2C2C]">₹{item.price * item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#E8E5E0] pt-4 space-y-2">
                {/* Coupon input */}
                <div className="pb-2" data-testid="checkout-coupon-block">
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2">
                      <div className="flex flex-col">
                        <span className="text-xs font-montserrat text-green-800 tracking-wider uppercase">Coupon applied</span>
                        <span className="text-sm font-seasons text-green-900">{appliedCoupon.code} · {appliedCoupon.discount_percent}% OFF</span>
                      </div>
                      <button
                        type="button"
                        onClick={removeCoupon}
                        data-testid="checkout-coupon-remove"
                        className="text-xs font-montserrat text-green-800 hover:text-green-900 underline"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <label className="block text-xs font-montserrat text-[#6B4E37] uppercase tracking-wider mb-1.5">Have a coupon?</label>
                      <div className="flex gap-2">
                        <Input
                          name="coupon"
                          value={couponInput}
                          onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }}
                          placeholder="e.g. WELCOME10"
                          className="bg-[#F5F3F0] border-none flex-1"
                          data-testid="checkout-coupon-input"
                          autoCapitalize="characters"
                        />
                        <Button
                          type="button"
                          onClick={() => applyCoupon()}
                          disabled={couponLoading || !couponInput.trim()}
                          data-testid="checkout-coupon-apply"
                          className="bg-[#6B4E37] hover:bg-[#5A4030] text-white font-montserrat tracking-wider uppercase text-xs"
                        >
                          {couponLoading ? '...' : 'Apply'}
                        </Button>
                      </div>
                      {couponError && (
                        <p className="text-xs text-red-600 font-montserrat mt-1" data-testid="checkout-coupon-error">{couponError}</p>
                      )}
                    </>
                  )}
                </div>

                <div className="flex justify-between text-sm font-montserrat">
                  <span className="text-[#8B7D6B]">Subtotal</span>
                  <span className="text-[#2C2C2C]">₹{subtotal}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm font-montserrat" data-testid="checkout-discount-row">
                    <span className="text-green-700">Discount ({appliedCoupon?.code})</span>
                    <span className="text-green-700">−₹{discountAmount}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-montserrat">
                  <span className="text-[#8B7D6B]">Shipping</span>
                  <span className="text-green-600">{shippingFee === 0 ? 'FREE' : `₹${shippingFee}`}</span>
                </div>
                <div className="flex justify-between text-lg font-seasons pt-2 border-t border-[#E8E5E0]">
                  <span className="text-[#2C2C2C]">Total</span>
                  <span className="text-[#2C2C2C]" data-testid="checkout-final-total">
                    ₹{finalTotal}
                  </span>
                </div>
              </div>

              <Button
                onClick={handlePayment}
                disabled={isProcessing || cartItems.length === 0}
                data-testid="checkout-place-order-btn"
                className="w-full mt-6 bg-[#C4B5A0] hover:bg-[#B0A090] text-white font-montserrat tracking-wider uppercase py-6"
              >
                {isProcessing
                  ? 'Processing...'
                  : paymentMethod === 'cod'
                    ? 'Place Order (Cash on Delivery)'
                    : 'Pay Now'}
              </Button>

              <p className="text-xs text-center text-[#8B7D6B] mt-4 font-montserrat">
                {paymentMethod === 'cod'
                  ? 'Pay in cash at your doorstep on delivery'
                  : 'Powered by Razorpay • 100% Secure'}
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
